import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, PaperPlaneTilt, Receipt } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-30: Invoice preview + send-now modal.
 *
 * Shows the current week's invoice line items (per-state × count × fee)
 * pulled from the /invoice-preview endpoint. Admin can force-send the
 * invoice email from here even outside the Monday 09:00 UTC window.
 */
export default function InvoicePreviewModal({ destination, onClose }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    setPreview(null);
    try {
      const { data } = await api.get(
        `/delivery/destinations/${destination.id}/invoice-preview`,
      );
      setPreview(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load invoice preview");
    }
  };

  useEffect(() => { load(); }, [destination.id]);

  const sendNow = async () => {
    if (!confirm(
      `Send the current-week invoice to ${preview?.recipient || "the buyer"}?`,
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/delivery/destinations/${destination.id}/invoice-send-now`,
      );
      if (data.status === "sent") {
        toast.success(
          `Invoice ${data.invoice_number} sent to ${data.recipient} · $${Number(data.grand_total).toFixed(2)}`,
        );
      } else if (data.status === "no_leads") {
        toast.info("No delivered leads in the invoice window — nothing to bill");
      } else if (data.status === "zero_billable") {
        toast.warning(
          `${data.n_delivered} lead(s) delivered but none had fees configured`,
        );
      } else if (data.status === "no_recipient") {
        toast.error("No recipient configured on this destination");
      } else {
        toast.info(`Invoice run: ${data.status}`);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const items = (preview?.line_items || []).filter((li) => li.count);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="invoice-preview-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white border-4 border-black w-full max-w-2xl p-6 brut-shadow-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="label-mono">Invoice preview</div>
            <h2 className="font-chivo font-black text-2xl uppercase tracking-tighter leading-tight">
              This week’s invoice
            </h2>
            <div className="text-xs text-zinc-600 mt-1">{destination.name}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="invoice-preview-close"
            className="border border-black p-1 hover:bg-black hover:text-white"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {err && (
          <div className="border border-red-600 bg-red-50 p-3 text-xs text-red-700">
            {err}
          </div>
        )}

        {!preview && !err && (
          <div className="label-mono text-zinc-500 py-8 text-center">
            Loading preview…
          </div>
        )}

        {preview && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 mb-4">
              <div className="border border-black p-3 bg-zinc-50">
                <div className="label-mono">Recipient</div>
                <div className="text-xs font-mono mt-1" data-testid="invoice-recipient">
                  {preview.recipient || "—"}
                </div>
              </div>
              <div className="border border-black p-3 bg-zinc-50">
                <div className="label-mono">Leads billed</div>
                <div className="font-chivo font-black text-2xl mt-1">
                  {preview.n_leads}
                </div>
              </div>
              <div className="border border-black p-3 bg-black text-white">
                <div className="label-mono text-zinc-400">Total due</div>
                <div className="font-chivo font-black text-2xl mt-1" data-testid="invoice-total">
                  ${Number(preview.grand_total).toFixed(2)}
                </div>
              </div>
            </div>

            <div className="text-[11px] text-zinc-600 mb-2">
              Period: {preview.period_start?.split("T")[0]} → {preview.period_end?.split("T")[0]} UTC
            </div>

            <div className="overflow-x-auto">
            <table className="w-full text-sm border border-black min-w-[400px]" data-testid="invoice-line-items">
              <thead>
                <tr className="border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
                  <th className="text-left py-2 px-3">State</th>
                  <th className="text-right py-2 px-3">Leads</th>
                  <th className="text-right py-2 px-3">Unit</th>
                  <th className="text-right py-2 px-3">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {items.map((li) => (
                  <tr key={li.state} className="border-b border-zinc-200" data-testid={`invoice-line-${li.state}`}>
                    <td className="py-2 px-3 font-mono font-bold">{li.state}</td>
                    <td className="py-2 px-3 text-right font-mono">{li.count}</td>
                    <td className="py-2 px-3 text-right font-mono">${Number(li.unit_fee).toFixed(2)}</td>
                    <td className="py-2 px-3 text-right font-mono font-bold">${Number(li.subtotal).toFixed(2)}</td>
                  </tr>
                ))}
                {!items.length && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center label-mono">
                      No billable line items in this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {preview.unbilled_states?.length > 0 && (
              <div className="mt-3 border border-amber-700 bg-amber-50 p-3 text-[11px] text-amber-900">
                ⚠ {preview.unbilled_states.length} state(s) delivered without a fee configured — not billed: <strong>{preview.unbilled_states.join(", ")}</strong>
              </div>
            )}

            {!preview.billable && (
              <div className="mt-3 border border-red-600 bg-red-50 p-3 text-[11px] text-red-800">
                This destination is <strong>not currently billable</strong>. Set payment_mode=postpay and enable Weekly invoice on the destination.
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="border border-black px-4 py-2 uppercase text-xs tracking-widest hover:bg-zinc-100"
              >
                Close
              </button>
              <button
                type="button"
                onClick={sendNow}
                disabled={busy || !preview.billable || preview.grand_total <= 0}
                data-testid="invoice-send-now-btn"
                className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-40 inline-flex items-center gap-2"
              >
                {busy ? <><Receipt size={12} /> Sending…</> : <><PaperPlaneTilt size={12} /> Send invoice now</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
